// src/encounter/lib/qr-item-meta.ts
// Per-item Questionnaire metadata the QR wire mappers need (PROD-1354c mapper fidelity):
//  • `type` drives decimal-vs-integer on save (a decimal item answered with a whole number must
//    still persist as valueDecimal — the numeric heuristic alone can't know).
//  • `options` (answerOption codings) drive the valueCoding round-trip for choice answers — the
//    DS renderer stores only the option KEY (`code ?? display`), so the coding is re-looked-up here.
//  • `repeats` drives the reload shape — a repeats (multi-select) item must hydrate as string[]
//    even for a single saved selection, or the DS renderer reads it as unselected.
//  • `readOnly` drives the SAVE FILTER (PROD-2129 D-S10b) — a read-only item is a READOUT, not a
//    question, and must never enter the emitted QuestionnaireResponse.
import type { FhirQuestionnaireItem, FhirCoding } from '@/types/template-studio-api';

export interface QrItemMeta {
  type: string;
  repeats: boolean;
  /** answerOption codings (choice/open-choice) for the valueCoding round-trip. */
  options: FhirCoding[];
  /**
   * The authored FHIR `readOnly` flag (PROD-2129).
   *
   * A read-only item is DISPLAY: the Studio's `readout` presents a computed or externally-sourced
   * value (a quantity with a LOINC code and a unit) that the clinician does not answer. The DS
   * renderer can still hold a value against its linkId — a calculatedExpression writes one — so
   * without this flag the autosave persisted the readout as if the clinician had entered it. That
   * provenance defect is the whole reason for the flag; a stale readout being PRESENTED as current
   * is not claimed, because the renderer recomputes a calc item's value on every render.
   *
   * The flag is the predicate BY CONTRACT (D-S10 b), not a proxy for "is computed": an item with a
   * `calculatedExpression` and no `readOnly: true` still persists its value as an answer,
   * deliberately. Absent/null on the wire reads as `false`: nothing is treated as read-only unless
   * the template says so. The save side is the only consumer — the read side
   * (`from-questionnaire-response-resource.ts`) is unfiltered, so a legacy row's readout linkId
   * hydrates once and is dropped by the next autosave.
   */
  readOnly: boolean;
  /**
   * The question text as authored (PROD-2608). Written onto the saved item (`QR.item.text`) so an
   * answer that later loses its question — templates are edited in place — can still be shown with
   * the question it answered, rather than an opaque linkId.
   */
  text?: string;
}

export type QrItemMetaMap = Map<string, QrItemMeta>;

/** Flatten the FHIR item tree (groups nest via `item`) into linkId → meta. */
export function buildQrItemMeta(items: FhirQuestionnaireItem[] | null | undefined): QrItemMetaMap {
  const map: QrItemMetaMap = new Map();
  const walk = (list: FhirQuestionnaireItem[]) => {
    for (const it of list) {
      map.set(it.linkId, {
        type: it.type,
        repeats: it.repeats === true,
        readOnly: it.readOnly === true,
        options: (it.answerOption ?? [])
          .map((o) => o.valueCoding)
          .filter((c): c is FhirCoding => c != null),
        ...(it.text?.trim() ? { text: it.text.trim() } : {}),
      });
      if (it.item) walk(it.item);
    }
  };
  walk(items ?? []);
  return map;
}

/** The DS renderer's answer key for a coding (its `optionValue`): `code ?? display`.
 *  NOTE (#work-reviews, PR #172): this key is only unique while answerOptions don't share a
 *  `code` (differing only by `display`). Current Template-Studio questionnaires satisfy that;
 *  if a questionnaire ever carries duplicate codes on one item, the reload would select the
 *  FIRST match — disambiguate (e.g. system+code) before extending to such content. */
export function codingKey(c: FhirCoding): string | undefined {
  return c.code ?? c.display ?? undefined;
}
